/**
 * @module utils
 * @private
 */

import { token } from "../web/store";
import { services } from "../constants";
import errors from "../web/errors/index";
import { setCookie, getCookieByName } from "./cookiesUtils";

const smarttv4k = "smarttv4k";
const externalDomainSecure = "https://starzplay-prod-ssl.akamaized.net";

function formatErrorObject(e) {
  const requestError = new Error();
  requestError.message =
    (e.response && (e.response.message || e.response.data.message)) || e.message || errors.genericNetworkError;
  requestError.status = (e.response && e.response.status) || e.status || 0;
  requestError.providerErrorCode =
    (e.response && (e.response.providerErrorCode || e.response.data.providerErrorCode)) || null;
  e.response && e.response.data && e.response.data.error_code && (requestError.error_code = e.response.data.error_code);
  // Saving in errorCode the same as in error_code to keep consistency in apps, error_code should be deprecated
  requestError.error_code && (requestError.errorCode = e.response.data.error_code);
  const errorData = e.response && e.response.data && e.response.data.data;
  forceLogoutBasedErrorObject(e);
  return {
    ...requestError,
    ...(errorData && { data: errorData })
  };
}

/**
 * This function is for force logout feature.
 */
function forceLogoutBasedErrorObject(e) {
  const httpCode = e.response && e.response.status;
  const httpStatusCode = e.response && e.response.data && e.response.data.error_code;
  if (httpCode === 403 || httpCode === 401) {
    setCookie("forcelogout", true);
  }
}

/** 
 *  this function finds the version of tizen from the user agent and check low and highend models
 */
function getTizenVersion() {
  let userAgent = navigator.userAgent;
  if (userAgent.indexOf("Tizen 2.4.0") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.4") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.3") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.2") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.1") > -1) {
    return false;
  }  else if (userAgent.indexOf("Tizen 2.0") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 1.0") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.3.1") > -1) {
    return false;
  }  else if (userAgent.indexOf("Tizen 2.3.2") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 2.2.1") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 4.0") > -1) {
    return false;
  } else if (userAgent.indexOf("Tizen 3.0") > -1) {
    return false;
  } else {
    return true;
  }
}

/**
 * Common helper methods for SDK as Mixin functions.
 */
export default {
  /**
   * Returns the asset types list as a String.
   * @returns {string}
   */
  getMediaAssetTypes(device) {
    if(device === 'webos' || device === 'vidaa' || (device === 'tizen' &&  getTizenVersion() === true ) )
      return "dash_widevine_spa,dash_t01_series_spa,dash_t01_movie_spa,filmstrip_bs,dfxp_en_spa,dfxp_ar_spa,dfxp_fr_spa";
    else
      return "hss_playready_spa,hss_t01_series_spa,hss_t01_movie_spa,filmstrip_bs,dfxp_en_spa,dfxp_ar_spa,dfxp_fr_spa";
  },

  getImageWidthQueryParams() {
    return Object.keys(this.imageWidths)
      .filter(imageType => this.imageWidths[imageType])
      .map(imageType => `&${imageType}Width=${this.imageWidths[imageType]}`)
      .join("");
  },

  async getDefaultPlatform(platform) {
    const defaultPlatform = platform || this.platformName;
    const platForm4K = await this.device.supports4K();
    return platForm4K ? smarttv4k : defaultPlatform;
  },

  /**
   * Creates a URL string for the service and passed params.
   *
   * @param category
   * @param service
   * @returns {string}
   */
  buildUrl(category, service) {
    return `${this.backendHosts[category]}${service}`;
  },

  parseQueryString(params) {
    return Object.keys(params)
      .reduce((queryParams, key) => {
        !!params[key] && queryParams.push(key + "=" + params[key]);
        return queryParams;
      }, [])
      .join("&");
  },

  parseQueryParamsString(data) {
    return Object.keys(data)
      .map(key => key + "=" + data[key])
      .join("&");
  },
  /**
   * Creates a URL string for the media services.
   * @param {*} category
   * @param {*} service
   * @param {*} id
   * @param {*} supports4K
   */
  async buildMediaUrl(category, service, id, supports4K = false, extraParams = {}) {
    const platformName = await this.getDefaultPlatform();
    const  device = await this.device.getPlatform();
    const queryString = this.parseQueryString(extraParams);
    return `${this.buildUrl(category, service)}${id || ""}?platform=${platformName}${
      supports4K ? `&supports4K=${supports4K}` : ""
    }&lang=${extraParams.lang ||
      this.lang}&mediaAssetTypes=${this.getMediaAssetTypes(device)}${this.getImageWidthQueryParams()}${queryString &&
      `&${queryString}`}
    `;
  },

  /**
   * Creates a URL with additional params object to add it to the trailing string for the service and passed params.
   *
   * @param category  The service category which can be media, auth... Etc
   * @param service   The service path
   * @param id  The id number to add it to the path before the query param
   * @param extraParams   list of extra params to add them to the trailing text of the search
   * @returns {string}
   */
  async buildUrlWithTrailingQuery(category, service, id, extraParams = {}) {
    extraParams["platform"] = await this.getDefaultPlatform(extraParams.platform);
    extraParams["lang"] = extraParams.lang || this.lang;
    extraParams["parentalControl"] = extraParams.parentalLevel || this.parentalLevel;

    const queryString = this.parseQueryString(extraParams);

    return `${this.buildUrl(category, service)}${id ? id : ""}?${queryString}${this.getImageWidthQueryParams()}`;
  },

  /**
   * Creates a URL with additional params object to add it to the trailing string for the service and passed params and adds the asset types.
   *
   * @param category  The service category which can be media, auth... Etc
   * @param service   The service path
   * @param id  The id number to add it to the path before the query param
   * @param extraParams   list of extra params to add them to the trailing text of the search
   * @returns {string}
   */
  async buildUrlWithTrailingQueryAndAssetTypes(category, service, id, extraParams = {}) {
    const  device = await this.device.getPlatform();
    const urlWithTrailingQuery = await this.buildUrlWithTrailingQuery(category, service, id, extraParams);
    return `${urlWithTrailingQuery}&mediaAssetTypes=${this.getMediaAssetTypes(device)}`;
  },

  /**
   * Creates a URL string for the reporting service.
   *
   * @example /reports/tizen2020?level=level&event=event&env=env
   *
   * @param origin
   * @param level
   * @param event
   * @param env
   * @param payload
   * @return {string}
   */
  buildUrlReport(origin, { level, event, env } = {}) {
    // TODO: Parametrize and/or specialize methods for different use cases. Better URL assembler
    return `${this.backendHosts.reports}${services.reports}/${origin}?level=${level}&event=${event}&env=${env}`;
  },

  /**
   * Returns user JWT in case it exists.
   */
  async getHeaders() {
    const userToken = (await token.getToken.call(this)) || (this.jwtCookie && getCookieByName(this.jwtCookie.name));
    return (userToken && userToken !== "undefined") ? { headers: { Authorization: userToken } } : null;
  },

  async buildLayoutHeaders(xToken = null, xQuery = null) {
    const userToken = await token.getToken.call(this);
    const extraHeaders = {
      headers: {
        ...(!!userToken && { Authorization: userToken })
      }
    };
    !!xToken && (extraHeaders.headers["x-token"] = xToken);
    !!xQuery && (extraHeaders.headers["x-query"] = xQuery);

    return extraHeaders;
  },

  /**
   * Returns data type.
   */
  typeOf(obj) {
    return {}.toString
      .call(obj)
      .slice(8, -1)
      .toLowerCase();
  },

  formatErrorObject,

  handleErrorResponse(e) {
    throw formatErrorObject(e);
  },

  // FIXME: This is a temporary solution to fix cross domain errors on filmstrip and external subtitles requests
  parseNonSecureMediaContentUrl(url) {
    const urlList = url.split("/").filter(item => item !== "");
    const protocol = urlList[0];
    const hostname = urlList[1];
    return protocol === "http:" ? url.replace(`${protocol}//${hostname}`, externalDomainSecure) : url;
  },

  /**
   * @description Returns a closured scoped function that checks
   * whether the passed value is different from the previous one,
   * usually in order to avoid unnecessary processing.
   * Alternatively, a custom checking function can be chained to the default check.
   * @param initValue Optional initial value.
   * @param fnCheck Optional checking function, will be passed old and new values
   * and chained with the basic checking in a OR way.
   * @returns {Function} The scoped function.
   */
  retainValueFactory(initValue, fnCheck) {
    let oldValue = !!initValue || initValue === 0 ? initValue : null;
    return newVal => {
      if (newVal !== oldValue || (!!fnCheck && fnCheck(oldValue, newVal))) {
        oldValue = newVal;
        return true;
      } else {
        return false;
      }
    };
  }
};
